import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { doc, onSnapshot, updateDoc, setDoc, arrayUnion, serverTimestamp, getDoc, runTransaction } from 'firebase/firestore';
import { db, appId, trackEvent, auth } from '../api/firebase.js';
import { appData } from '../data';
import { trackStickerUnlocked } from '../utils/speakrootsAnalytics.js';
import offlineManager from '../utils/offlineManager.js';
import { sendDay4ProgressEmail, sendDay6TrialExpiryEmail } from '../utils/epic3Emails';
import { smtp2goService } from '../api/smtp2go';
import logger from '../utils/logger';
import { mergeProfileWithLanguage, getLanguageProgress, initializeLanguageProgress, calculateTotalXp, getActiveLanguages, isLanguageSpecificField } from '../utils/multiLanguageProgress';

export const useProgress = (user) => {
    const [progress, setProgress] = useState(null);
    const welcomeEmailSentRef = useRef(false);
    const [isLoading, setIsLoading] = useState(true);
    
    // 🛡️ Concurrency guards for transaction-based operations
    const completeOnboardingRef = useRef(false);
    const addProfileRef = useRef(false);
    const setSubscriptionStatusRef = useRef(false);
    const completeModuleRef = useRef(false);

    const activeProfile = useMemo(() => {
        if (!progress || !progress.profiles) {
            return null;
        }

        const activeProfileId = progress.lastActiveProfileId || progress.activeProfileId;
        if (!activeProfileId) {
            const profileKeys = Object.keys(progress.profiles);
            if (profileKeys.length > 0) {
                // Fallback to the first profile if no active one is set
                const fallbackProfile = progress.profiles[profileKeys[0]];
                if (fallbackProfile) {
                    // Apply dynamic state derivation to fallback profile too
                    const getDerivedState = (p) => {
                        // A subscribed user always has full access
                        if (progress.subscriptionStatus === 'subscribed' || progress.subscriptionStatus === 'active') {
                            return 'subscribed';
                        }

                        // If trialEndDate is not set, they are in a trial
                        if (!p.trialEndDate) {
                            return 'trial';
                        }

                        // Compare trialEndDate with the current time
                        const now = new Date();
                        const trialEnd = new Date(p.trialEndDate);

                        if (now > trialEnd) {
                            return 'trial_expired';
                        } else {
                            return 'trial';
                        }
                    };

                    const derivedState = getDerivedState(fallbackProfile);
                    logger.info('State Derivation', 'Derived state calculated for fallback profile');
                    
                    // MIGRATION: Convert old 'lastViewedMonth' to new 'lastViewedUnit'
                    const migratedProfile = { ...fallbackProfile };
                    if (migratedProfile.lastViewedMonth !== undefined) {
                        logger.info('MIGRATION', 'Migrating lastViewedMonth to lastViewedUnit');
                        // Set the new field from the old one
                        migratedProfile.lastViewedUnit = migratedProfile.lastViewedMonth;
                        // Delete the old field from the object we use in the app
                        delete migratedProfile.lastViewedMonth;
                        
            // Note: Database migration removed from useMemo to avoid circular dependency
            // This migration will happen automatically on next profile update
        }
                    
                    return {
                        ...migratedProfile,
                        userState: derivedState
                    };
                }
            }
            return null;
        }

        const profile = progress.profiles[activeProfileId];
        if (!profile) {
            return null;
        }

        // --- START: NEW DYNAMIC STATE DERIVATION ---
        const getDerivedState = (p) => {
            // A subscribed user always has full access
            if (progress.subscriptionStatus === 'subscribed' || progress.subscriptionStatus === 'active') {
                return 'subscribed';
            }

            // If trialEndDate is not set, they are in a trial
            if (!p.trialEndDate) {
                return 'trial';
            }

            // Compare trialEndDate with the current time
            const now = new Date();
            const trialEnd = new Date(p.trialEndDate);

            if (now > trialEnd) {
                return 'trial_expired';
            } else {
                return 'trial';
            }
        };

        const derivedState = getDerivedState(profile);
        logger.info('State Derivation', 'Derived state calculated for active profile');
        // --- END: NEW DYNAMIC STATE DERIVATION ---

        // MIGRATION: Convert old 'lastViewedMonth' to new 'lastViewedUnit'
        const migratedProfile = { ...profile };
        if (migratedProfile.lastViewedMonth !== undefined) {
            logger.info('MIGRATION', 'Migrating lastViewedMonth to lastViewedUnit for active profile');
            // Set the new field from the old one
            migratedProfile.lastViewedUnit = migratedProfile.lastViewedMonth;
            // Delete the old field from the object we use in the app
            delete migratedProfile.lastViewedMonth;
            
            // Note: Database migration removed from useMemo to avoid circular dependency
            // This migration will happen automatically on next profile update
        }

        // MULTI-LANGUAGE: Get currentLanguage (persisted or default to first language)
        const currentLanguage = migratedProfile.currentLanguage || 
                               (migratedProfile.progress ? Object.keys(migratedProfile.progress)[0] : null) ||
                               'Twi'; // Ultimate fallback
        
        // MULTI-LANGUAGE: Merge base profile with language-specific progress
        const mergedProfile = mergeProfileWithLanguage(migratedProfile, currentLanguage);
        
        // Return the profile object with the dynamically calculated userState
        return {
            ...mergedProfile,
            userState: derivedState, // This overwrites the potentially stale state from the DB
            currentLanguage // Ensure currentLanguage is set
        };

    }, [progress]);

    useEffect(() => {

        // Check if we should use local storage for progress
        // Only use localStorage if explicitly in mock mode OR on localhost without real auth
        const useRealAuth = localStorage.getItem('speak-roots-use-real-auth') === 'true';
        const useLocalStorage = (window.location.hostname === 'localhost' && !useRealAuth) || 
                               localStorage.getItem('speak-roots-use-mock-auth') === 'true';
        
        if (useLocalStorage) {
            console.log('Using local storage for progress on:', window.location.hostname);
            if (!user) {
                setProgress(null);
                setIsLoading(false);
                return;
            }
            
            // Check if we have persisted progress data in localStorage
            const persistedProgress = localStorage.getItem('speak-roots-dev-progress');
            if (persistedProgress) {
                try {
                    const progressData = JSON.parse(persistedProgress);
                    console.log('Development: Loading persisted progress data:', progressData);
                    setProgress(progressData);
                    setIsLoading(false);
                    return;
                } catch (error) {
                    logger.error('App', 'Development: Failed to parse persisted progress data', error);
                    localStorage.removeItem('speak-roots-dev-progress');
                }
            }
            
            // If we have a user, create progress data (for localhost or netlify)
            console.log('Creating progress data for user:', user.uid);
            const profileId = window.location.hostname === 'localhost' ? 'dev-profile-1' : `profile-${user.uid}`;
            const mockProgress = {
                profiles: {
                    [profileId]: {
                        id: profileId,
                        childName: window.location.hostname === 'localhost' ? 'Dev Child' : 'New User',
                        name: window.location.hostname === 'localhost' ? 'Dev Child' : 'New User', // Keep both for compatibility
                        age: 8,
                        avatar: '👦',
                        currentLanguage: 'Twi',
                        xp: 0,
                        streak: 0,
                        completedLessons: [],
                        completedQuizzes: [],
                        completedStories: [],
                        completedActivities: [],
                        hasSeenLanguageToggleTooltip: true,
                        hasCompletedStoryPreview: false,
                        weakWords: [],
                        lastActiveDate: new Date().toISOString()
                    }
                },
                lastActiveProfileId: profileId,
                subscriptionStatus: 'active', // Set to active in development mode for testing
                onboardingComplete: true
            };
            
            // Save to localStorage for persistence
            localStorage.setItem('speak-roots-dev-progress', JSON.stringify(mockProgress));
            setProgress(mockProgress);
            setIsLoading(false);
            return;
        }

        if (!user) {
            setProgress(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        const docRef = doc(db, 'artifacts', appId, 'users', user.uid, 'progress', 'user_data');

        // Add a timeout fallback to ensure loading is set to false
        const loadingTimeout = setTimeout(() => {
            logger.warn('useProgress', 'Progress loading timeout after 10 seconds');
            setIsLoading(false);
        }, 10000); // 10 second timeout

        const unsubscribe = onSnapshot(docRef, async (docSnap) => {
            if (docSnap.exists()) {
                const progressData = docSnap.data();
                console.log('Progress document found:', progressData);
                console.log('Progress document structure:', {
                    onboardingComplete: progressData.onboardingComplete,
                    profiles: progressData.profiles,
                    lastActiveProfileId: progressData.lastActiveProfileId,
                    activeProfileId: progressData.activeProfileId
                });
                console.log('Profiles object:', progressData.profiles);
                console.log('Profiles keys:', Object.keys(progressData.profiles || {}));
                console.log('Active profile ID:', progressData.lastActiveProfileId || progressData.activeProfileId);
                // Normalize missing trialEndDate if we can infer it
                try {
                    const needsBackfill = progressData.trialStartDate && !progressData.trialEndDate;
                    if (needsBackfill) {
                        const startMs = progressData.trialStartDate.seconds
                            ? progressData.trialStartDate.seconds * 1000
                            : new Date(progressData.trialStartDate).getTime();
                        const inferredEnd = new Date(startMs + 7 * 24 * 60 * 60 * 1000).toISOString();
                        const docRefUpdate = doc(db, 'artifacts', appId, 'users', user.uid, 'progress', 'user_data');
                        updateDoc(docRefUpdate, { trialEndDate: inferredEnd })
                            .then(() => logger.info('Normalization', 'Trial end date normalized successfully'))
                            .catch((e) => logger.warn('Normalization', 'Failed to normalize trial end date', e));
                        progressData.trialEndDate = inferredEnd; // reflect locally
                    }
                } catch (e) {
                    logger.warn('Normalization', 'Error during trial date normalization', e);
                }

                setProgress(progressData);
                setIsLoading(false);
                clearTimeout(loadingTimeout); // Clear the timeout since we got the data
                
                // Save to offline storage
                await offlineManager.saveProgress(progressData);
            } else {
                // No progress document exists - try to load from offline storage first
                console.log('No progress document found for user, checking offline storage...');
                
                try {
                    const offlineProgress = await offlineManager.getProgress();
                    if (offlineProgress) {
                        console.log('Loading progress from offline storage:', offlineProgress);
                        setProgress(offlineProgress);
                        setIsLoading(false);
                        clearTimeout(loadingTimeout); // Clear the timeout since we got the data
                        
                        // Try to sync back to Firebase in the background
                        setDoc(docRef, offlineProgress).then(() => {
                            console.log('Offline progress synced to Firebase');
                        }).catch((error) => {
                            console.warn('Failed to sync offline progress to Firebase:', error);
                        });
                        return;
                    }
                } catch (error) {
                    console.warn('Failed to load offline progress:', error);
                }
                
                // Create initial progress immediately to prevent infinite loading
                console.log('Creating initial progress document...');
                const initialProgress = {
                    email: user.email,
                    accountEmail: user.email, // Ensure accountEmail is set for nurture emails
                    onboardingComplete: false,
                    profiles: {},
                    lastActiveProfileId: null,
                    subscriptionStatus: 'trial', // FIX: New users must start in a trial
                    trialStartDate: serverTimestamp(), // FIX: Use serverTimestamp for consistency
                    trialEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // Ensure end date exists
                };
                
                // Set the progress immediately so the app doesn't get stuck
                setProgress(initialProgress);
                setIsLoading(false);
                clearTimeout(loadingTimeout); // Clear the timeout since we got the data
                
                // Create the document in Firestore in the background
                setDoc(docRef, initialProgress).then(() => {
                    console.log('Initial progress document created in Firestore');
                }).catch((error) => {
                    logger.error('App', 'Failed to create initial progress document', error);
                });
            }
        }, (error) => {
            logger.error('App', 'Firestore snapshot error', error);
            setIsLoading(false);
            clearTimeout(loadingTimeout); // Clear the timeout on error
        });

        return () => unsubscribe();
    }, [user]);

    const getDocRef = useCallback(() => {
        console.log('getDocRef called with user:', user ? { uid: user.uid } : null);
        console.log('getDocRef - user type:', typeof user, 'user value:', user);
        if (!user) {
            console.log("User not available for progress updates (development mode).");
            return null;
        }
        if (!db) {
            console.log("Firebase db not available (development mode).");
            return null;
        }
        const docRef = doc(db, 'artifacts', appId, 'users', user.uid, 'progress', 'user_data');
        console.log('Created docRef:', docRef.path);
        return docRef;
    }, [user]);

    // --- START: NEW EPIC 3 EMAIL TRIGGER LOGIC ---
    useEffect(() => {
        // Ensure we have a valid user, progress, and that we are not in mock mode
        const useRealAuth = localStorage.getItem('speak-roots-use-real-auth') === 'true';
        if (!user || !progress || !progress.onboardingComplete || !user.email || !useRealAuth) {
            return;
        }

        const { emailCampaigns, trialStartDate, profiles, lastActiveProfileId, accountName } = progress;
        const activeProfile = profiles?.[lastActiveProfileId];
        
        logger.info('useProgress', 'Checking email campaign eligibility for user');

        if (!trialStartDate || !activeProfile) {
            return;
        }

        // Check if we need to run email logic
        const { welcomeSent, midTrialSent, trialExpirySent } = emailCampaigns || {};
        
        // --- Welcome Email (send only for new users, not existing logins) ---
        if (!welcomeSent && !welcomeEmailSentRef.current) {
            // Check if this is a new user by looking at the user's creation time
            // If the user was created more than 5 minutes ago, they're likely an existing user logging in
            const userCreationTime = user.metadata?.creationTime;
            const now = Date.now();
            const fiveMinutesAgo = now - (5 * 60 * 1000);
            // ✅ FIX: Convert creationTime string to timestamp before comparing
            const userCreationTimestamp = userCreationTime ? new Date(userCreationTime).getTime() : 0;
            const isNewUser = userCreationTimestamp && userCreationTimestamp > fiveMinutesAgo;
            
            console.log('Welcome email check:', {
                email: user.email,
                userCreationTime: userCreationTime,
                userCreationTimestamp: userCreationTimestamp,
                now: now,
                fiveMinutesAgo: fiveMinutesAgo,
                isNewUser: isNewUser,
                welcomeSent: welcomeSent
            });
            
            // Only send welcome email for truly new users
            if (!isNewUser) {
                console.log('⚠️ Skipping welcome email - existing user logging in');
                // Mark as sent to prevent future checks
                welcomeEmailSentRef.current = true;
                const docRef = doc(db, 'artifacts', appId, 'users', user.uid, 'progress', 'user_data');
                updateDoc(docRef, {
                    'emailCampaigns.welcomeSent': true
                }).catch(error => {
                    logger.error('App', 'Failed to mark welcome email as sent for existing user', error);
                });
                return;
            }
            
            console.log('Sending contextual welcome email for new user...');
            console.log('Email data:', {
                email: user.email,
                accountName: accountName,
                userDisplayName: user.displayName,
                fallbackName: accountName || user.displayName || 'User',
                profilesCount: profiles ? Object.keys(profiles).length : 0,
                profiles: profiles
            });
            
            // Don't send email if profiles data is not available yet
            if (!profiles || Object.keys(profiles).length === 0) {
                console.log('⚠️ Skipping welcome email - profiles data not available yet');
                return;
            }
            
            // Mark as sent immediately to prevent duplicate sends in this render cycle
            welcomeEmailSentRef.current = true;
            
            // Check if user has completed their first lesson
            const hasCompletedFirstLesson = activeProfile?.completedLessons && activeProfile.completedLessons.length > 0;
            logger.info('useProgress', `Preparing welcome email, hasCompletedFirstLesson: ${hasCompletedFirstLesson}`);
            
            // Use a more robust approach to prevent duplicate sends
            const docRef = doc(db, 'artifacts', appId, 'users', user.uid, 'progress', 'user_data');
            
            // First, atomically update the flag to prevent race conditions
            updateDoc(docRef, {
                'emailCampaigns.welcomeSent': true
            }).then(() => {
                // Only send email after the flag is successfully updated
                return smtp2goService.sendWelcomeEmail(
                    user.email, 
                    accountName || user.displayName || 'User',
                    accountName,
                    profiles,
                    hasCompletedFirstLesson
                );
            }).then(() => {
                console.log('✅ Contextual welcome email sent successfully');
            }).catch(error => {
                logger.error('App', 'Failed to send contextual welcome email', error);
                // Reset both the ref and the flag if email failed so it can be retried
                welcomeEmailSentRef.current = false;
                updateDoc(docRef, {
                    'emailCampaigns.welcomeSent': false
                }).catch(updateError => {
                    logger.error('App', 'Failed to reset welcome email status', updateError);
                });
            });
        }
        
        if (midTrialSent && trialExpirySent) {
            return; // All campaign emails have been sent
        }

        const trialStart = new Date(trialStartDate.seconds ? trialStartDate.seconds * 1000 : trialStartDate);
        const now = new Date();
        const daysSinceStart = Math.floor((now - trialStart) / (1000 * 60 * 60 * 24));
        
        const docRef = doc(db, 'artifacts', appId, 'users', user.uid, 'progress', 'user_data');

        // Note: Day 4 and Day 6 emails are now handled by the automated cron job
        // at /api/send-trial-nurture-emails which runs daily at 12:00 PM UTC (Noon)
        // This ensures emails are sent even when users are not actively using the app

    }, [progress, user]); // This effect runs whenever progress or user data changes
    
    // Reset welcome email ref when user changes
    useEffect(() => {
        welcomeEmailSentRef.current = false;
    }, [user?.uid]);
    // --- END: NEW EPIC 3 EMAIL TRIGGER LOGIC ---

    // 🛡️ TRANSACTION-BASED ONBOARDING (PREVENTS RACE CONDITIONS & DUPLICATE PROFILES)
    const completeOnboarding = useCallback(async (childName, ageGroup, language, user = null) => {
        // Check if we should use local storage
        const useRealAuth = localStorage.getItem('speak-roots-use-real-auth') === 'true';
        const useLocalStorage = (window.location.hostname === 'localhost' && !useRealAuth) || 
                               localStorage.getItem('speak-roots-use-mock-auth') === 'true';
        
        if (useLocalStorage) {
            console.log('Development mode: Simulating onboarding completion', useRealAuth ? '(real auth mode)' : '(mock mode)');
            // Use crypto.randomUUID() for guaranteed uniqueness
            const profileId = crypto.randomUUID();
            
            const firstProfile = {
                id: profileId,
                childName,
                ageGroup,
                currentLanguage: language,
                completedLessons: [],
                completedUnits: [],
                xp: 0,
                streak: 1,
                lastLoginDate: new Date().toISOString().split('T')[0],
                expandedUnits: [1, 2, 3],
                lastViewedUnit: 1,
                inProgressModule: null,
                hasCompletedStoryPreview: false,
                // NEW TRIAL-RELATED FIELDS
                userState: 'trial',
                trialStartDate: new Date().toISOString(),
                trialEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
                hasSeenDashboardWelcome: false,
                firstLessonCompleted: false,
                gameZonePlaysToday: 0,
                lastGameZonePlayDate: null,
                cliffhangerReached: false,
                cliffhangerDate: null,
                createdAt: new Date().toISOString()
            };
            
            const progressData = {
                onboardingComplete: true,
                subscriptionStatus: 'trial', // Set to trial in development mode for testing
                trialStartDate: new Date(),
                hasSeenExpiredModal: false,
                soundSettings: { isMusicEnabled: true, areLessonSoundsEnabled: true },
                profiles: { [profileId]: firstProfile },
                lastActiveProfileId: profileId,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
                emailPreferences: { promotions: true, reports: 'weekly' },
                // NEW TRIAL MANAGEMENT FIELDS
                trialManagement: {
                    activeTrials: [profileId],
                    expiredTrials: [],
                    convertedTrials: []
                },
                emailCampaigns: {
                    welcomeSent: false,
                    midTrialSent: false,
                    trialExpirySent: false
                }
            };
            
            console.log('Development: Setting mock progress data:', progressData);
            setProgress(progressData);
            setIsLoading(false); // CRITICAL: Set loading to false to prevent infinite loading
            
            // Persist progress data to localStorage for development mode
            localStorage.setItem('speak-roots-dev-progress', JSON.stringify(progressData));
            console.log('Development: Progress data persisted to localStorage');
            
            console.log('Development: Onboarding completed successfully');
            return;
        }

        // ========== PRODUCTION MODE: TRANSACTION-BASED ONBOARDING ==========
        // Use provided user parameter (from auth) or fall back to context user
        const currentUser = user || auth.currentUser;
        if (!currentUser) {
            logger.error('App', 'No user available for onboarding completion');
            throw new Error('No user available for onboarding');
        }
        
        // 🛡️ CONCURRENCY GUARD: Prevent multiple simultaneous onboarding attempts
        if (completeOnboardingRef.current) {
            logger.info('completeOnboarding', 'Onboarding already in progress, blocking concurrent attempt');
            return { success: false, error: 'Onboarding already in progress' };
        }
        
        completeOnboardingRef.current = true;
        
        // Generate UUID for guaranteed uniqueness
        const profileId = crypto.randomUUID();
        const operationId = `onboard_${currentUser.uid}_${Date.now()}`;
        
        // 🛡️ NEW: Set operation flag BEFORE transaction starts
        localStorage.setItem('onboardingOperationId', operationId);

        logger.info('completeOnboarding', `Starting onboarding transaction with operation ID: ${operationId}`);

        try {
            await runTransaction(db, async (transaction) => {
                // ========== READ PHASE ==========
        const docRef = doc(db, 'artifacts', appId, 'users', currentUser.uid, 'progress', 'user_data');
                const currentDoc = await transaction.get(docRef);

                // ========== VALIDATION ==========
                // Check if onboarding already completed
                if (currentDoc.exists() && currentDoc.data().onboardingComplete) {
                    logger.warn('completeOnboarding', 'Onboarding already completed, aborting transaction');
                    throw new Error('Onboarding already completed');
                }

                // ========== BUILD PROFILE DATA ==========
        // MULTI-LANGUAGE: Initialize progress structure with first language
        const firstLanguageProgress = initializeLanguageProgress(language);
        
        const firstProfile = {
            id: profileId,
            childName,
            ageGroup,
            currentLanguage: language, // Last active language
            // MULTI-LANGUAGE: Progress is now keyed by language
            progress: {
                [language]: firstLanguageProgress
            },
            // Profile-level fields (not language-specific)
            streak: 1,
            longestStreak: 0,
            lastLoginDate: new Date().toISOString().split('T')[0],
            userState: 'trial',
            hasSeenDashboardWelcome: false,
            gameZonePlaysToday: 0,
            lastGameZonePlayDate: null,
            cliffhangerReached: false,
            cliffhangerDate: null,
            unlockedStickers: [],
            weakWords: [],
            activityLog: [],
            dailyQuest: {
                completed: 0,
                target: 2,
                date: new Date().toISOString().split('T')[0]
            },
            recentCompletions: [],
            createdAt: serverTimestamp(),
            lastActive: serverTimestamp()
        };

                // ========== BUILD PROGRESS DATA ==========
            const progressData = {
                onboardingComplete: true,
                email: currentUser.email,
                accountEmail: currentUser.email,
                accountName: currentUser.displayName || (typeof childName === 'string' ? childName : 'User'),
                subscriptionStatus: 'trial',
                subscriptionOnboardingSeen: false,  // FIX #27: Add missing field
                trialStartDate: serverTimestamp(),
                trialEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                profiles: { [profileId]: firstProfile },
                lastActiveProfileId: profileId,
                hasSeenExpiredModal: false,
                soundSettings: {
                    isMusicEnabled: true,
                    areLessonSoundsEnabled: true
                },
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
                emailPreferences: {
                    promotions: true,
                    reports: 'weekly'
                },
                trialManagement: {
                    activeTrials: [profileId],
                    expiredTrials: [],
                    convertedTrials: []
                },
                emailCampaigns: {
                    welcomeSent: false,
                    midTrialSent: false,
                    trialExpirySent: false
                },
                operationId,
                onboardedAt: serverTimestamp(),
                lastUpdated: serverTimestamp()
            };

                // ========== WRITE PHASE ==========
                // Parent user document (for collection queries)
                const userDocRef = doc(db, 'artifacts', appId, 'users', currentUser.uid);
                transaction.set(userDocRef, {
                    uid: currentUser.uid,
                    email: currentUser.email,
                    displayName: progressData.accountName,
                    createdAt: serverTimestamp(),
                    lastUpdated: serverTimestamp()
                }, { merge: true });

                // Progress document
                transaction.set(docRef, progressData, { merge: true });

                logger.info('completeOnboarding', 'Transaction completed, user and progress documents created');
            });

            logger.info('completeOnboarding', 'Onboarding completed successfully');
            
            // Clear operation flag on success
            localStorage.removeItem('onboardingOperationId');
            
            return { success: true, profileId, operationId };
            
        } catch (error) {
            logger.error('completeOnboarding', 'Failed to complete onboarding', error);
            
            // Clear operation flag on failure too
            localStorage.removeItem('onboardingOperationId');
            
            if (error.message === 'Onboarding already completed') {
                // Not a critical error - onboarding was already done
                return { success: true, alreadyCompleted: true };
            }
            
            throw error;
        } finally {
            // Always reset the concurrency guard
            completeOnboardingRef.current = false;
        }
    }, [db]);

    // 🛡️ TRANSACTION-BASED PROFILE CREATION (PREVENTS RACE CONDITIONS)
    const addProfile = useCallback(async (childName, ageGroup, language) => {
        // Check if we should use local storage
        const useRealAuth = localStorage.getItem('speak-roots-use-real-auth') === 'true';
        const useLocalStorage = (window.location.hostname === 'localhost' && !useRealAuth) || 
                               localStorage.getItem('speak-roots-use-mock-auth') === 'true';
        
        if (useLocalStorage) {
            console.log('Development mode: Creating new profile locally');
            // Use crypto.randomUUID() for guaranteed uniqueness
            const newProfileId = crypto.randomUUID();
            
            const newProfile = { 
                id: newProfileId, 
                childName, 
                ageGroup, 
                currentLanguage: language, 
                completedLessons: [], 
                completedUnits: [], 
                xp: 0, 
                streak: 1, 
                lastLoginDate: new Date().toISOString().split('T')[0],
                expandedUnits: [1, 2, 3],
                lastViewedUnit: 1,
                inProgressModule: null,
                hasCompletedStoryPreview: false,
                // NEW TRIAL-RELATED FIELDS
                userState: 'trial',
                trialStartDate: new Date().toISOString(),
                trialEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
                hasSeenDashboardWelcome: false,
                firstLessonCompleted: false,
                gameZonePlaysToday: 0,
                lastGameZonePlayDate: null,
                cliffhangerReached: false,
                cliffhangerDate: null,
                createdAt: new Date().toISOString() // Track creation time
            };
            
            // Update local progress data
            setProgress(prev => {
                const newProgress = {
                    ...prev,
                    profiles: {
                        ...prev.profiles,
                        [newProfileId]: newProfile
                    },
                    lastActiveProfileId: newProfileId
                };
                
                // Persist to localStorage
                localStorage.setItem('speak-roots-dev-progress', JSON.stringify(newProgress));
                console.log('Development: New profile created and persisted:', newProfile);
                
                return newProgress;
            });
            
            return { success: true, profileId: newProfileId };
        }
        
        // ========== PRODUCTION MODE: TRANSACTION-BASED CREATION ==========
        const docRef = getDocRef();
        if (!docRef) {
            return { success: false, error: 'No document reference' };
        }

        // Generate UUID for guaranteed uniqueness
        const newProfileId = crypto.randomUUID();

        logger.info('addProfile', `Starting profile creation with ID: ${newProfileId}`);

        // 🛡️ CONCURRENCY GUARD: Prevent multiple simultaneous profile creation attempts
        if (addProfileRef.current) {
            logger.info('addProfile', 'Profile creation already in progress, blocking concurrent attempt');
            return { success: false, error: 'Profile creation already in progress' };
        }
        
        addProfileRef.current = true;

        try {
            await runTransaction(db, async (transaction) => {
                // ========== READ PHASE ==========
                const doc = await transaction.get(docRef);
                if (!doc.exists()) {
                    throw new Error('User document not found');
                }

                const data = doc.data();
                const existingProfiles = data.profiles || {};

                // ========== VALIDATION ==========
                // Check if a profile with this childName already exists
                const duplicateName = Object.values(existingProfiles).some(
                    p => p.childName.toLowerCase() === childName.toLowerCase()
                );

                if (duplicateName) {
                    throw new Error(`A profile named "${childName}" already exists`);
                }

                // Check profile limit (e.g., max 5 profiles)
                const MAX_PROFILES = 5;
                if (Object.keys(existingProfiles).length >= MAX_PROFILES) {
                    throw new Error(`Maximum of ${MAX_PROFILES} profiles reached`);
                }

                // ========== WRITE PHASE ==========
        const newProfile = { 
            id: newProfileId, 
            childName, 
            ageGroup, 
            currentLanguage: language, 
            completedLessons: [], 
            completedUnits: [], 
            xp: 0, 
            streak: 1, 
            lastLoginDate: new Date().toISOString().split('T')[0],
            expandedUnits: [1, 2, 3],
            lastViewedUnit: 1,
            inProgressModule: null,
            hasCompletedStoryPreview: false,
            // NEW TRIAL-RELATED FIELDS
                    userState: data.subscriptionStatus || 'trial',
                    trialStartDate: data.trialStartDate || new Date().toISOString(),
                    trialEndDate: data.trialEndDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            hasSeenDashboardWelcome: false,
            firstLessonCompleted: false,
            gameZonePlaysToday: 0,
            lastGameZonePlayDate: null,
            cliffhangerReached: false,
            cliffhangerDate: null,
                    activityLog: [],
                    createdAt: serverTimestamp(),
                    lastActive: serverTimestamp()
                };

                // Update document atomically
                transaction.update(docRef, {
                    [`profiles.${newProfileId}`]: newProfile,
                    lastActiveProfileId: newProfileId,
                    lastUpdated: serverTimestamp()
                });

                logger.info('addProfile', `Profile created successfully with ID: ${newProfileId}`);
            });

            return { success: true, profileId: newProfileId };

        } catch (error) {
            logger.error('addProfile', 'Failed to add profile', error);
            
            // Return user-friendly error messages
            if (error.message.includes('already exists')) {
                return { success: false, error: error.message, userMessage: error.message };
            } else if (error.message.includes('Maximum')) {
                return { success: false, error: error.message, userMessage: error.message };
            } else {
                return { 
                    success: false, 
                    error: error.message, 
                    userMessage: 'Failed to create profile. Please try again.' 
                };
            }
        } finally {
            // Always reset the concurrency guard
            addProfileRef.current = false;
        }
    }, [getDocRef, db]);

    // NEW GENERIC FUNCTION TO UPDATE A PROFILE FIELD
    const updateProfileState = useCallback(async (key, value, profileId = null) => {
        // Check if we should use local storage
        const useRealAuth = localStorage.getItem('speak-roots-use-real-auth') === 'true';
        const useLocalStorage = (window.location.hostname === 'localhost' && !useRealAuth) || 
                               localStorage.getItem('speak-roots-use-mock-auth') === 'true';
        
        const targetProfileId = profileId || activeProfile?.id;
        if (!targetProfileId) return;
        
        // MULTI-LANGUAGE: Determine if this is a language-specific field
        const isLanguageSpecific = isLanguageSpecificField(key);
        const currentLanguage = activeProfile?.currentLanguage || 'Twi';
        
        if (useLocalStorage) {
            console.log('Development: Updating profile state:', key, '=', value, 'for profile:', targetProfileId);
            if (!progress?.profiles?.[targetProfileId]) return;
            
            const profile = progress.profiles[targetProfileId];
            
            if (isLanguageSpecific) {
                // Update language-specific field
                if (!profile.progress) {
                    profile.progress = {};
                }
                if (!profile.progress[currentLanguage]) {
                    profile.progress[currentLanguage] = initializeLanguageProgress(currentLanguage);
                }
                profile.progress[currentLanguage][key] = value;
            } else {
                // Update profile-level field
                profile[key] = value;
            }
            
            const newProgress = {
                ...progress,
                profiles: {
                    ...progress.profiles,
                    [targetProfileId]: profile
                }
            };
            
            setProgress(newProgress);
            localStorage.setItem('speak-roots-dev-progress', JSON.stringify(newProgress));
            console.log('Development: Profile state updated and persisted');
            return;
        }
        
        // Production: Update in Firestore
        const docRef = getDocRef();
        if (!docRef) return;
        
        // MULTI-LANGUAGE: Use language-specific path if needed
        const updatePath = isLanguageSpecific
            ? `profiles.${targetProfileId}.progress.${currentLanguage}.${key}`
            : `profiles.${targetProfileId}.${key}`;
        
        return updateDoc(docRef, { [updatePath]: value });
    }, [getDocRef, activeProfile, progress, setProgress]);

    // MULTI-LANGUAGE: Switch to a different language
    const switchLanguage = useCallback(async (languageId, profileId = null) => {
        const targetProfileId = profileId || activeProfile?.id;
        if (!targetProfileId) return { success: false, error: 'No profile selected' };
        
        // Check if we should use local storage
        const useRealAuth = localStorage.getItem('speak-roots-use-real-auth') === 'true';
        const useLocalStorage = (window.location.hostname === 'localhost' && !useRealAuth) || 
                               localStorage.getItem('speak-roots-use-mock-auth') === 'true';
        
        if (useLocalStorage) {
            console.log('Development: Switching language to:', languageId);
            if (!progress?.profiles?.[targetProfileId]) return { success: false, error: 'Profile not found' };
            
            const profile = progress.profiles[targetProfileId];
            
            // Initialize progress structure if it doesn't exist
            if (!profile.progress) {
                profile.progress = {};
            }
            
            // Initialize language progress if it doesn't exist
            if (!profile.progress[languageId]) {
                profile.progress[languageId] = initializeLanguageProgress(languageId);
            }
            
            // Update currentLanguage
            profile.currentLanguage = languageId;
            
            const newProgress = {
                ...progress,
                profiles: {
                    ...progress.profiles,
                    [targetProfileId]: profile
                }
            };
            
            setProgress(newProgress);
            localStorage.setItem('speak-roots-dev-progress', JSON.stringify(newProgress));
            console.log('Development: Language switched and persisted');
            return { success: true };
        }
        
        // Production: Update in Firestore
        const docRef = getDocRef();
        if (!docRef) return { success: false, error: 'No document reference' };
        
        try {
            // Get current profile to check if progress structure exists
            const doc = await getDoc(docRef);
            if (!doc.exists()) {
                return { success: false, error: 'Profile not found' };
            }
            
            const data = doc.data();
            const profile = data.profiles?.[targetProfileId];
            if (!profile) {
                return { success: false, error: 'Profile not found' };
            }
            
            // Initialize progress structure if needed
            const updates = {};
            if (!profile.progress) {
                updates[`profiles.${targetProfileId}.progress`] = {};
            }
            
            // Initialize language progress if needed
            const currentProgress = profile.progress || {};
            if (!currentProgress[languageId]) {
                updates[`profiles.${targetProfileId}.progress.${languageId}`] = initializeLanguageProgress(languageId);
            }
            
            // Update currentLanguage
            updates[`profiles.${targetProfileId}.currentLanguage`] = languageId;
            
            await updateDoc(docRef, updates);
            logger.info('switchLanguage', `Switched to ${languageId}`);
            return { success: true };
        } catch (error) {
            logger.error('switchLanguage', 'Failed to switch language', error);
            return { success: false, error: error.message };
        }
    }, [getDocRef, activeProfile, progress, setProgress]);

    // MULTI-LANGUAGE: Add a new language to the profile
    const addLanguage = useCallback(async (languageId, profileId = null) => {
        const targetProfileId = profileId || activeProfile?.id;
        if (!targetProfileId) return { success: false, error: 'No profile selected' };
        
        // Check if we should use local storage
        const useRealAuth = localStorage.getItem('speak-roots-use-real-auth') === 'true';
        const useLocalStorage = (window.location.hostname === 'localhost' && !useRealAuth) || 
                               localStorage.getItem('speak-roots-use-mock-auth') === 'true';
        
        if (useLocalStorage) {
            console.log('Development: Adding language:', languageId);
            if (!progress?.profiles?.[targetProfileId]) return { success: false, error: 'Profile not found' };
            
            const profile = progress.profiles[targetProfileId];
            
            // Initialize progress structure if it doesn't exist
            if (!profile.progress) {
                profile.progress = {};
            }
            
            // Check if language already exists
            if (profile.progress[languageId]) {
                return { success: false, error: 'Language already added' };
            }
            
            // Initialize language progress
            profile.progress[languageId] = initializeLanguageProgress(languageId);
            
            const newProgress = {
                ...progress,
                profiles: {
                    ...progress.profiles,
                    [targetProfileId]: profile
                }
            };
            
            setProgress(newProgress);
            localStorage.setItem('speak-roots-dev-progress', JSON.stringify(newProgress));
            console.log('Development: Language added and persisted');
            return { success: true };
        }
        
        // Production: Update in Firestore
        const docRef = getDocRef();
        if (!docRef) return { success: false, error: 'No document reference' };
        
        try {
            const doc = await getDoc(docRef);
            if (!doc.exists()) {
                return { success: false, error: 'Profile not found' };
            }
            
            const data = doc.data();
            const profile = data.profiles?.[targetProfileId];
            if (!profile) {
                return { success: false, error: 'Profile not found' };
            }
            
            // Check if language already exists
            if (profile.progress?.[languageId]) {
                return { success: false, error: 'Language already added' };
            }
            
            // Initialize progress structure if needed
            const updates = {};
            if (!profile.progress) {
                updates[`profiles.${targetProfileId}.progress`] = {};
            }
            
            // Add new language progress
            updates[`profiles.${targetProfileId}.progress.${languageId}`] = initializeLanguageProgress(languageId);
            
            await updateDoc(docRef, updates);
            logger.info('addLanguage', `Added language ${languageId}`);
            return { success: true };
        } catch (error) {
            logger.error('addLanguage', 'Failed to add language', error);
            return { success: false, error: error.message };
        }
    }, [getDocRef, activeProfile, progress, setProgress]);

    // NEW FUNCTION TO UPDATE IN-PROGRESS MODULES
    const updateInProgressModule = useCallback(async (moduleId, currentStep) => {
        const docRef = getDocRef();
        if (!docRef || !activeProfile) return;
        const payload = { moduleId, currentStep };
        return updateDoc(docRef, { [`profiles.${activeProfile.id}.inProgressModule`]: payload });
    }, [getDocRef, activeProfile]);

    // NEW FUNCTION TO CLEAR IN-PROGRESS MODULES ON COMPLETION
    const clearInProgressModule = useCallback(async () => {
        const docRef = getDocRef();
        if (!docRef || !activeProfile) return;
        return updateDoc(docRef, { [`profiles.${activeProfile.id}.inProgressModule`]: null });
    }, [getDocRef, activeProfile]);


    const updateWeakWords = useCallback(async (updatedList) => {
        // Check if we should use local storage
        const useRealAuth = localStorage.getItem('speak-roots-use-real-auth') === 'true';
        const useLocalStorage = (window.location.hostname === 'localhost' && !useRealAuth) || 
                               localStorage.getItem('speak-roots-use-mock-auth') === 'true';
        
        if (useLocalStorage) {
            console.log('Development: updateWeakWords called with:', updatedList);
            if (!activeProfile) return;
            
            const updatedProfile = {
                ...activeProfile,
                weakWords: updatedList
            };
            
            setProgress(prev => {
                const newProgress = {
                    ...prev,
                    profiles: {
                        ...prev.profiles,
                        [activeProfile.id]: updatedProfile
                    }
                };
                localStorage.setItem('speak-roots-dev-progress', JSON.stringify(newProgress));
                return newProgress;
            });
            
            console.log('Development: Weak words updated locally');
            return;
        }
        
        const docRef = getDocRef();
        if (!docRef || !activeProfile) return;
        return updateDoc(docRef, { [`profiles.${activeProfile.id}.weakWords`]: updatedList });
    }, [getDocRef, activeProfile]);
    
    const setCurrentActiveProfile = useCallback(async (profileId) => {
        const docRef = getDocRef();
        if (!docRef) return;
        return updateDoc(docRef, { lastActiveProfileId: profileId });
    }, [getDocRef]);

    const updateSoundSetting = useCallback(async (settingKey) => {
        const docRef = getDocRef();
        if (!docRef || !progress) return;
        const newSettings = { ...progress.soundSettings, [settingKey]: !progress.soundSettings[settingKey] };
        return updateDoc(docRef, { soundSettings: newSettings });
    }, [getDocRef, progress]);
    
    const enableAllSounds = useCallback(async () => {
        const docRef = getDocRef();
        if (!docRef) return;
        return updateDoc(docRef, { soundSettings: { isMusicEnabled: true, areLessonSoundsEnabled: true } });
    }, [getDocRef]);

    const updateLearningSettings = useCallback(async (mode, age) => {
        const docRef = getDocRef();
        if (!docRef || !activeProfile) return;
        return updateDoc(docRef, {
            [`profiles.${activeProfile.id}.learningMode`]: mode,
            [`profiles.${activeProfile.id}.ageGroup`]: age
        });
    }, [getDocRef, activeProfile]);
    
    // 🛡️ SUBSCRIPTION STATE MACHINE (PREVENTS RACE CONDITIONS & INVALID TRANSITIONS)
    const SUBSCRIPTION_STATES = {
        trial: {
            canTransitionTo: ['active', 'trial_expired'],
            requiresPayment: false
        },
        trial_expired: {
            canTransitionTo: ['active', 'trial'],
            requiresPayment: true
        },
        active: {
            canTransitionTo: ['trial_expired', 'cancelled'],
            requiresPayment: false
        },
        cancelled: {
            canTransitionTo: ['active'],
            requiresPayment: true
        }
    };

    const setSubscriptionStatus = useCallback(async (newStatus, metadata = {}) => {
        logger.info('setSubscriptionStatus', `Attempting to set subscription status to: ${newStatus}`);
        
        const { paymentId, reason, source = 'user' } = metadata;
        
        // Generate idempotency key for payment operations
        const idempotencyKey = paymentId || `${user?.uid}_${newStatus}_${Date.now()}`;
        
        const docRef = getDocRef();
        if (!docRef) {
            logger.error('setSubscriptionStatus', 'No document reference available');
            return { success: false, error: 'No document reference' };
        }
        
        // 🛡️ CONCURRENCY GUARD: Prevent multiple simultaneous subscription status updates
        if (setSubscriptionStatusRef.current) {
            logger.info('setSubscriptionStatus', 'Subscription update already in progress, blocking concurrent attempt');
            return { success: false, error: 'Subscription update already in progress' };
        }
        
        setSubscriptionStatusRef.current = true;
        
        try {
            const result = await runTransaction(db, async (transaction) => {
                // ========== READ PHASE ==========
                const doc = await transaction.get(docRef);
                if (!doc.exists()) {
                    throw new Error('User document not found');
                }
                
                const data = doc.data();
                const currentStatus = data.subscriptionStatus || 'trial';
                
                logger.info('setSubscriptionStatus', `Current status: ${currentStatus}, transitioning to: ${newStatus}`);
                
                // ========== IDEMPOTENCY CHECK ==========
                // Check for duplicate operation within last 5 minutes
                const recentTransitions = data.subscriptionTransitions || [];
                const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
                const duplicate = recentTransitions.some(t => 
                    t.idempotencyKey === idempotencyKey &&
                    t.timestamp > fiveMinutesAgo
                );
                
                if (duplicate) {
                    logger.info('setSubscriptionStatus', 'Duplicate operation detected, skipping');
                    return { duplicate: true, status: currentStatus };
                }
                
                // If already in target state, skip (unless it's a retry with payment)
                if (currentStatus === newStatus && !paymentId) {
                    logger.info('setSubscriptionStatus', `Already in target state: ${currentStatus}`);
                    return { alreadyInState: true, status: currentStatus };
                }
                
                // ========== STATE MACHINE VALIDATION ==========
                const validTransitions = SUBSCRIPTION_STATES[currentStatus]?.canTransitionTo || [];
                if (!validTransitions.includes(newStatus)) {
                    const error = `Invalid subscription transition: ${currentStatus} → ${newStatus}. Valid: ${validTransitions.join(', ')}`;
                    logger.error('setSubscriptionStatus', 'Invalid subscription state transition', error);
                    throw new Error(error);
                }
                
                // ========== PAYMENT VALIDATION ==========
                // Transitions from expired/cancelled to active require payment
                if (SUBSCRIPTION_STATES[currentStatus].requiresPayment && newStatus === 'active') {
                    if (!paymentId) {
                        throw new Error('Payment ID required for this transition');
                    }
                    
                    // Verify payment hasn't been used before (prevents double-activation)
                    const usedPayments = data.usedPaymentIds || [];
                    if (usedPayments.includes(paymentId)) {
                        throw new Error('Payment ID already used');
                    }
                }
                
                // ========== BUILD TRANSITION RECORD ==========
                const transition = {
                    from: currentStatus,
                    to: newStatus,
                    timestamp: Date.now(),
                    idempotencyKey,
                    source, // 'user', 'webhook', 'admin', 'cron', etc.
                    userId: user?.uid
                };
                
                // Only add optional fields if they have values (Firebase doesn't allow undefined)
                if (reason) transition.reason = reason;
                if (paymentId) transition.paymentId = paymentId;
                
                // ========== PREPARE UPDATES ==========
                const updates = {
                    subscriptionStatus: newStatus,
                    subscriptionLastUpdated: serverTimestamp()
                };
                
                // Add transition to history (keep last 50)
                const updatedTransitions = [...recentTransitions, transition].slice(-50);
                updates.subscriptionTransitions = updatedTransitions;
                
                // Track payment if provided
                if (paymentId) {
                    const usedPayments = data.usedPaymentIds || [];
                    updates.usedPaymentIds = [...usedPayments, paymentId];
                    updates.lastPaymentId = paymentId;
                    updates.lastPaymentDate = serverTimestamp();
                }
                
                // ========== HANDLE SPECIFIC TRANSITIONS ==========
                
                // Trial → Active (conversion)
                if (currentStatus === 'trial' && newStatus === 'active') {
                    updates.trialConvertedAt = serverTimestamp();
                    updates.subscriptionStartDate = serverTimestamp();
                    
                    // Move profile from trial to converted in trial management
                    const lastProfileId = data.lastActiveProfileId;
                    if (lastProfileId) {
                        const trialMgmt = data.trialManagement || {};
                        const activeTrials = trialMgmt.activeTrials || [];
                        const convertedTrials = trialMgmt.convertedTrials || [];
                        
                        updates['trialManagement.activeTrials'] = activeTrials.filter(id => id !== lastProfileId);
                        updates['trialManagement.convertedTrials'] = [...convertedTrials, lastProfileId];
                    }
                }
                
                // Trial_Expired → Active (conversion after expiry)
                if (currentStatus === 'trial_expired' && newStatus === 'active') {
                    updates.trialConvertedAt = serverTimestamp();
                    updates.subscriptionStartDate = serverTimestamp();
                    updates.expiredTrialReactivatedAt = serverTimestamp();
                }
                
                // Any Status → Trial_Expired
                if (newStatus === 'trial_expired') {
                    updates.trialExpiredAt = serverTimestamp();
                }
                
                // Active → Cancelled
                if (currentStatus === 'active' && newStatus === 'cancelled') {
                    updates.subscriptionCancelledAt = serverTimestamp();
                    updates.subscriptionCancelReason = reason || 'user_initiated';
                }
                
                // Cancelled → Active (reactivation)
                if (currentStatus === 'cancelled' && newStatus === 'active') {
                    updates.subscriptionReactivatedAt = serverTimestamp();
                    updates.subscriptionCancelReason = null; // Clear cancel reason
                }
                
                // ========== APPLY ATOMICALLY ==========
                transaction.update(docRef, updates);
                
                logger.info('setSubscriptionStatus', 'Subscription status transaction completed successfully');
                return { 
                    success: true, 
                    transition,
                    previousStatus: currentStatus,
                    newStatus
                };
            });
            
            // ========== POST-TRANSACTION ==========
            if (result.success) {
                logger.info('setSubscriptionStatus', `Subscription status changed: ${result.previousStatus} → ${result.newStatus}`);
                
                // Track analytics
                trackEvent('subscription_status_changed', {
                    from: result.previousStatus,
                    to: result.newStatus,
                    source: metadata.source
                });
                
                // Handle trial conversion tracking
                if (result.previousStatus === 'trial' && result.newStatus === 'active') {
                    trackEvent('trial_converted', {
                        paymentId: metadata.paymentId
                    });
                }
            }
            
            return result;
            
        } catch (error) {
            logger.error('setSubscriptionStatus', 'Failed to update subscription status', error);
            
            // Return structured error for UI handling
            return {
                success: false,
                error: error.message,
                code: error.code
            };
        } finally {
            // Always reset the concurrency guard
            setSubscriptionStatusRef.current = false;
        }
    }, [getDocRef, user, db]);

    const setSubscriptionOnboardingSeen = useCallback(async (seen) => {
        const docRef = getDocRef();
        if (!docRef) return;
        return updateDoc(docRef, { subscriptionOnboardingSeen: seen });
    }, [getDocRef]);

    // NEW FUNCTION TO UPDATE EMAIL PREFERENCES
    const updateEmailPreferences = useCallback(async (preferences) => {
        const docRef = getDocRef();
        if (!docRef) return;
        return updateDoc(docRef, { 
            emailPreferences: preferences,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'
        });
    }, [getDocRef]);

    // To: useProgress -> completeModule (Enhanced for Offline Support)
    const completeModule = useCallback(async (module, score) => {
        console.log('completeModule: Function called');
        console.log('completeModule: window.location.hostname:', window.location.hostname);
        console.log('completeModule: Is localhost?', window.location.hostname === 'localhost');
        console.log('completeModule: Is netlify?', window.location.hostname.includes('netlify.app'));
        
        // Check if we should use local storage
        const useRealAuth = localStorage.getItem('speak-roots-use-real-auth') === 'true';
        const useLocalStorage = (window.location.hostname === 'localhost' && !useRealAuth) || 
                               localStorage.getItem('speak-roots-use-mock-auth') === 'true';
        
        if (useLocalStorage) {
            console.log('Development mode: Simulating module completion');
            console.log('Development: completeModule called with:', { module, score });
            console.log('Development: activeProfile:', activeProfile);
            console.log('Development: module.id:', module?.id);
            console.log('Development: current completedLessons:', activeProfile?.completedLessons);
            console.log('Development: module already completed?', activeProfile?.completedLessons?.includes(module?.id));
            console.log('Development: Looking for module ID:', module?.id);
            console.log('Development: In completedLessons array:', activeProfile?.completedLessons);
            console.log('Development: Array includes check result:', activeProfile?.completedLessons?.includes(module?.id));
            
            if (!activeProfile || !module || activeProfile.completedLessons?.includes(module.id)) {
                console.log('Development: Early return from completeModule - module already completed or missing data');
                return null;
            }
            
            // MULTI-LANGUAGE: Get current language and ensure progress structure exists
            const currentLanguage = activeProfile.currentLanguage || 'Twi';
            const profile = progress.profiles[activeProfile.id];
            
            // Initialize progress structure if needed
            if (!profile.progress) {
                profile.progress = {};
            }
            if (!profile.progress[currentLanguage]) {
                profile.progress[currentLanguage] = initializeLanguageProgress(currentLanguage);
            }
            
            const langProgress = profile.progress[currentLanguage];
            
            // Update language-specific progress
            const updatedLangProgress = {
                ...langProgress,
                completedLessons: [...(langProgress.completedLessons || []), module.id],
                xp: (langProgress.xp || 0) + (score || 10),
                inProgressModule: null
            };
            
            // Update profile with new language progress
            const updatedProfile = {
                ...profile,
                progress: {
                    ...profile.progress,
                    [currentLanguage]: updatedLangProgress
                },
                currentLanguage // Ensure currentLanguage is set
            };
            
            console.log('Development: Adding module to completedLessons:', module.id, 'Type:', module.type);
            console.log('Development: Updated completedLessons:', updatedProfile.completedLessons);
            
            // Check for quest completion (quizzes only)
            // NOTE: dailyQuest is profile-level, not language-specific
            let questJustCompleted = false;
            if (module.type === 'quiz') {
                const quest = profile.dailyQuest || activeProfile.dailyQuest || { completed: 0, target: 2 };
                const newCompletedCount = (quest.completed || 0) + 1;
                updatedProfile.dailyQuest = { ...quest, completed: newCompletedCount };
                
                if (quest.completed < quest.target && newCompletedCount >= quest.target) {
                    updatedLangProgress.xp += 20; // Bonus XP for quest completion
                    updatedProfile.progress[currentLanguage] = updatedLangProgress;
                    questJustCompleted = true;
                }
            }
            
            // Check if unit is complete and return sticker reward
            const languageData = appData[currentLanguage];
            const unitData = languageData.learningPath.find(m => m.unit === module.unit);
            if (unitData) {
                const completableTypes = ['lesson', 'quiz', 'activity', 'story', 'chapterReview'];
                const completableItemsInUnit = unitData.lessons.filter(l => completableTypes.includes(l.type));
                const allCompletableItemIds = completableItemsInUnit.map(l => l.id);
                const currentCompletedSet = new Set([...updatedLangProgress.completedLessons]);
                
                console.log('Development: Checking unit completion for unit', unitData.unit);
                console.log('Development: Completable items in unit:', allCompletableItemIds);
                console.log('Development: Current completed set:', Array.from(currentCompletedSet));
                console.log('Development: All items completed?', allCompletableItemIds.every(id => currentCompletedSet.has(id)));
                console.log('Development: Unit already completed?', updatedProfile.completedUnits?.includes(unitData.unit));
                
                // Only trigger achievement modal if ALL items in the unit are completed
                if (allCompletableItemIds.every(id => currentCompletedSet.has(id)) && !updatedLangProgress.completedUnits?.includes(unitData.unit)) {
                    updatedLangProgress.completedUnits = [...(updatedLangProgress.completedUnits || []), unitData.unit];
                    updatedProfile.progress[currentLanguage] = updatedLangProgress;
                    const sticker = languageData.stickerArt.find(s => s.unit === unitData.unit);
                    if (sticker) {
                        // Update the progress state locally
                        setProgress(prev => {
                            const newProgress = {
                                ...prev,
                                profiles: {
                                    ...prev.profiles,
                                    [activeProfile.id]: updatedProfile
                                }
                            };
                            console.log('Development: Updating progress state for unit completion:', newProgress);
                            
                            // Persist updated progress data to localStorage
                            localStorage.setItem('speak-roots-dev-progress', JSON.stringify(newProgress));
                            console.log('Development: Unit completion progress data persisted to localStorage');
                            
                            return newProgress;
                        });
                        
                        console.log('Development: Unit completed, returning sticker reward');
                        console.log('Development: Sticker URL:', sticker.unlocked);
                        console.log('Development: Theme title:', unitData.theme);
                        return { stickerUrl: sticker.unlocked, themeTitle: unitData.theme, questJustCompleted };
                    }
                } else {
                    console.log('Development: Unit not yet complete, no sticker reward');
                }
            }
            
            // Update the progress state locally
            setProgress(prev => {
                const newProgress = {
                    ...prev,
                    profiles: {
                        ...prev.profiles,
                        [activeProfile.id]: updatedProfile
                    }
                };
                console.log('Development: Updating progress state:', newProgress);
                
                // Persist updated progress data to localStorage
                localStorage.setItem('speak-roots-dev-progress', JSON.stringify(newProgress));
                console.log('Development: Updated progress data persisted to localStorage');
                
                return newProgress;
            });
            
            console.log('Development: Module completed successfully');
            console.log('Development: Returning questJustCompleted only, no sticker reward');
            return { questJustCompleted };
        }
        
        console.log('completeModule: Going to production mode');
        const docRef = getDocRef();
        if (!docRef || !activeProfile || !module) {
            console.log('completeModule: Early return - missing data');
            return null;
        }

        // 🛡️ TRANSACTION-BASED COMPLETION (PREVENTS RACE CONDITIONS)
        // Generate idempotency key to prevent duplicate completions
        const completionId = `${activeProfile.id}_${module.id}_${Date.now()}`;
        
        // 🛡️ CONCURRENCY GUARD: Prevent multiple simultaneous module completions
        if (completeModuleRef.current) {
            logger.info('completeModule', 'Module completion already in progress, blocking concurrent attempt');
            return { success: false, reason: 'Module completion already in progress' };
        }
        
        completeModuleRef.current = true;
        
        try {
            const result = await runTransaction(db, async (transaction) => {
                // ========== READ PHASE ==========
                const doc = await transaction.get(docRef);
                if (!doc.exists()) {
                    throw new Error('Progress document not found');
                }
                
                const data = doc.data();
                const profile = data.profiles[activeProfile.id];
                
                if (!profile) {
                    throw new Error(`Profile ${activeProfile.id} not found`);
                }
                
                // ========== IDEMPOTENCY CHECK ==========
                // Check if module already completed (prevents duplicate XP)
                if (profile.completedLessons && profile.completedLessons.includes(module.id)) {
                    logger.info('completeModule', `Module ${module.id} already completed, skipping`);
                    return { alreadyCompleted: true, questJustCompleted: false };
                }
                
                // Check for recent completion attempt (within 5 minutes - prevents double-tap bugs)
                const recentCompletions = profile.recentCompletions || [];
                const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
                const isDuplicate = recentCompletions.some(c => 
                    c.moduleId === module.id && c.timestamp > fiveMinutesAgo
                );
                
                if (isDuplicate) {
                    logger.info('completeModule', `Duplicate completion attempt detected for module ${module.id}`);
                    return { duplicate: true, questJustCompleted: false };
                }
                
                // ========== CALCULATE NEW VALUES (SERVER-SIDE) ==========
                // Use current server data, not stale client state
                const currentXP = profile.xp || 0;
                const currentCompletedLessons = profile.completedLessons || [];
                const quest = profile.dailyQuest || { completed: 0, target: 2 };
                
                let finalScore = score || 10;
                let questJustCompleted = false;
                
                // Handle daily quest for quizzes
                let newQuestCompleted = quest.completed;
        if (module.type === 'quiz') {
                    newQuestCompleted = (quest.completed || 0) + 1;

                    // Quest completion bonus
                    if (quest.completed < quest.target && newQuestCompleted >= quest.target) {
                finalScore += 20; // Bonus XP for quest completion
                questJustCompleted = true;
                    }
                }
                
                // ========== WRITE PHASE (ATOMIC) ==========
                const updates = {};
                
                // Update completed lessons (use array replacement in transaction)
                // This is CORRECT: transaction re-reads on retry, so currentCompletedLessons is always fresh
                // arrayUnion would BREAK the idempotency check at line 1382
                updates[`profiles.${activeProfile.id}.completedLessons`] = [...currentCompletedLessons, module.id];
                
                // Update XP (calculated from current value, not stale)
                updates[`profiles.${activeProfile.id}.xp`] = currentXP + finalScore;
                
                // Clear in-progress module
                updates[`profiles.${activeProfile.id}.inProgressModule`] = null;
                
                // Update quest progress
                if (module.type === 'quiz') {
                    updates[`profiles.${activeProfile.id}.dailyQuest.completed`] = newQuestCompleted;
                }
                
                // Activity log
                updates[`profiles.${activeProfile.id}.activityLog`] = [
                    ...(profile.activityLog || []),
                    {
                        moduleId: module.id,
                        timestamp: new Date().toISOString(),
                        completionId
                    }
                ];
                
                // Track recent completions for idempotency
                const updatedRecentCompletions = [
                    ...recentCompletions.filter(c => c.timestamp > fiveMinutesAgo), // Keep only recent
                    {
                        moduleId: module.id,
                        completionId,
                        timestamp: Date.now(),
                        score: finalScore
                    }
                ];
                updates[`profiles.${activeProfile.id}.recentCompletions`] = updatedRecentCompletions.slice(-20); // Keep last 20
                
                // Update last viewed unit (only if module has a unit - Game Zone activities don't)
                if (module.unit !== undefined && module.unit !== null) {
                    updates[`profiles.${activeProfile.id}.lastViewedUnit`] = module.unit;
                }
                
                // Update last activity timestamp
                updates[`profiles.${activeProfile.id}.lastActive`] = serverTimestamp();
                
                // Apply all updates atomically
                transaction.update(docRef, updates);
                
                // ========== CHECK UNIT COMPLETION ==========
                const languageData = appData[profile.currentLanguage];
        const unitData = languageData.learningPath.find(m => m.unit === module.unit);

                if (unitData) {
        const completableTypes = ['lesson', 'quiz', 'activity', 'story', 'chapterReview'];
                    const completableItems = unitData.lessons.filter(l => completableTypes.includes(l.type));
                    const allCompletableIds = completableItems.map(l => l.id);
                    const newCompletedSet = new Set([...currentCompletedLessons, module.id]);
                    
                    const unitComplete = allCompletableIds.every(id => newCompletedSet.has(id));
                    const unitNotYetMarkedComplete = !(profile.completedUnits || []).includes(unitData.unit);
                    
                    if (unitComplete && unitNotYetMarkedComplete) {
                        // Mark unit as complete
                        transaction.update(docRef, {
                            [`profiles.${activeProfile.id}.completedUnits`]: [
                                ...(profile.completedUnits || []),
                                unitData.unit
                            ]
                        });
                        
            const sticker = languageData.stickerArt.find(s => s.unit === unitData.unit);
                        
                        return {
                            success: true,
                            unitCompleted: true,
                            stickerUrl: sticker?.unlocked,
                            themeTitle: unitData.theme,
                            questJustCompleted,
                            completionId
                        };
                    }
                }
                
                return {
                    success: true,
                    questJustCompleted,
                    completionId
                };
            });
            
            // ========== POST-TRANSACTION ==========
            
            // Track analytics
            if (result.questJustCompleted) {
                trackEvent('daily_quest_completed');
            }
            
            if (result.unitCompleted) {
                trackStickerUnlocked(module.unit, activeProfile.currentLanguage);
            }
            
            // Cleanup old recent completions (async, non-blocking)
            setTimeout(() => {
                cleanupRecentCompletions(docRef, activeProfile.id);
            }, 5000);
            
            logger.info('completeModule', `Module ${module.id} completed successfully`);
            return result;
            
        } catch (error) {
            logger.error('completeModule', `Failed to complete module ${module.id}`, error);
            
            // Handle network errors - queue for offline sync
            if (error.code === 'unavailable' || error.code === 'failed-precondition') {
                logger.info('completeModule', 'Network error detected, queuing for offline sync');
                
                await offlineManager.queueForSync('lesson_completion', {
                    profileId: activeProfile.id,
                    moduleId: module.id,
                    score,
                    moduleType: module.type,
                    unit: module.unit,
                    timestamp: Date.now(),
                    completionId
                }, 'critical'); // High priority
                
                // Return success to UI (will sync later)
                return { 
                    success: true, 
                    offline: true,
                    questJustCompleted: false // Can't calculate without server data
                };
            }
            
            // Other errors - rethrow
            throw error;
        } finally {
            // Always reset the concurrency guard
            completeModuleRef.current = false;
        }
    }, [getDocRef, activeProfile]);
    
    // Helper function to cleanup old recent completions
    const cleanupRecentCompletions = async (docRef, profileId) => {
        try {
            const doc = await getDoc(docRef);
            const profile = doc.data().profiles[profileId];
            const recentCompletions = profile?.recentCompletions || [];
            
            // Keep only last 50 completions
            if (recentCompletions.length > 50) {
                const trimmed = recentCompletions
                    .sort((a, b) => b.timestamp - a.timestamp)
                    .slice(0, 50);
                
                await updateDoc(docRef, {
                    [`profiles.${profileId}.recentCompletions`]: trimmed
                });
                
                logger.info('cleanupRecentCompletions', `Cleaned up recent completions for profile ${profileId}`);
            }
        } catch (error) {
            logger.error('cleanupRecentCompletions', 'Failed to cleanup recent completions', error);
            // Non-critical, don't throw
        }
    };
    
    // 🛡️ TRANSACTION-BASED STREAK UPDATE (PREVENTS RACE CONDITIONS & TIMEZONE ISSUES)
    const updateDailyStreakRef = useRef(false); // Prevent concurrent calls
    const updateDailyStreak = useCallback(async () => {
        // Prevent concurrent calls
        if (updateDailyStreakRef.current) {
            logger.info('updateDailyStreak', 'Streak update already in progress, skipping');
            return;
        }

        // Check if we should use local storage
        const useRealAuth = localStorage.getItem('speak-roots-use-real-auth') === 'true';
        const useLocalStorage = (window.location.hostname === 'localhost' && !useRealAuth) || 
                               localStorage.getItem('speak-roots-use-mock-auth') === 'true';
        
        if (useLocalStorage) {
            console.log('Development mode: Skipping daily streak update');
            return;
        }

        const docRef = getDocRef();
        if (!docRef || !activeProfile) return;

        updateDailyStreakRef.current = true;
        logger.info('updateDailyStreak', 'Starting daily streak update');

        try {
            const result = await runTransaction(db, async (transaction) => {
                // ========== READ PHASE ==========
                const doc = await transaction.get(docRef);
                if (!doc.exists()) {
                    throw new Error('User document not found');
                }

                const data = doc.data();
                const profile = data.profiles[activeProfile.id];

                if (!profile) {
                    throw new Error(`Profile ${activeProfile.id} not found`);
                }

                // ========== TIMEZONE-AWARE DATE CALCULATION ==========
                // Use user's timezone from profile (or default to UTC)
                const userTimezone = data.timezone || 'UTC';
                
                // Get today's date in user's timezone
                const now = new Date();
                const today = new Intl.DateTimeFormat('en-CA', { // 'en-CA' gives YYYY-MM-DD format
                    timeZone: userTimezone,
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                }).format(now).replace(/\//g, '-'); // Format: YYYY-MM-DD

                // Get yesterday's date in user's timezone
                const yesterdayDate = new Date(now.getTime() - 86400000);
                const yesterday = new Intl.DateTimeFormat('en-CA', {
                    timeZone: userTimezone,
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                }).format(yesterdayDate).replace(/\//g, '-');

                const lastLogin = profile.lastLoginDate;

                logger.info('updateDailyStreak', `Checking streak for timezone: ${userTimezone}, today: ${today}, last login: ${lastLogin}`);

                // ========== IDEMPOTENCY CHECK ==========
                // If already updated today, skip
                if (lastLogin === today) {
                    logger.info('updateDailyStreak', 'Streak already updated today, skipping');
                    return { skipped: true, streak: profile.streak };
                }

                // Handle future dates (clock skew, timezone changes, etc)
                if (lastLogin && lastLogin > today) {
                    logger.info('updateDailyStreak', 'Last login date is in the future, skipping to avoid clock skew issues');
                    // Treat future date as today to avoid breaking streak unfairly
                    return { skipped: true, streak: profile.streak };
                }

                // ========== CALCULATE NEW STREAK ==========
                let newStreak;
                let streakAction;

                if (lastLogin === yesterday) {
                    // Consecutive day - increment streak
                    newStreak = (profile.streak || 0) + 1;
                    streakAction = 'continued';
                    logger.info('updateDailyStreak', `Streak continued: ${newStreak} days`);
                } else {
                    // Streak broken - reset to 1
                    const oldStreak = profile.streak || 0;
                    newStreak = 1;
                    streakAction = 'broken';
                    logger.info('updateDailyStreak', `Streak broken: was ${oldStreak} days, now reset to 1`);

                    // Track broken streak in history (if it was significant)
                    if (oldStreak > 0 && lastLogin) {
                        const streakHistory = profile.streakHistory || [];
                        const historyEntry = {
                            length: oldStreak,
                            endDate: lastLogin,
                            brokenDate: today,
                            timestamp: Date.now()
                        };

                        // Keep last 10 streak histories
                        const updatedHistory = [...streakHistory, historyEntry].slice(-10);

                        transaction.update(docRef, {
                            [`profiles.${activeProfile.id}.streakHistory`]: updatedHistory
                        });

                        // Track broken streak analytics
                        trackEvent('streak_broken', {
                            length: oldStreak,
                            profileId: activeProfile.id
                        });
                    }
                }

                // ========== WRITE PHASE ==========
                const updates = {
                [`profiles.${activeProfile.id}.streak`]: newStreak,
                [`profiles.${activeProfile.id}.lastLoginDate`]: today,
                    [`profiles.${activeProfile.id}.dailyQuest`]: {
                        completed: 0,
                        target: 2,
                        date: today
                    },
                    [`profiles.${activeProfile.id}.lastActive`]: serverTimestamp()
                };

                // Track longest streak
                const longestStreak = profile.longestStreak || 0;
                if (newStreak > longestStreak) {
                    updates[`profiles.${activeProfile.id}.longestStreak`] = newStreak;
                    
                    // Track new longest streak achievement
                    trackEvent('longest_streak_achieved', {
                        streak: newStreak,
                        profileId: activeProfile.id
                    });
                }

                // Apply atomically
                transaction.update(docRef, updates);

                logger.info('updateDailyStreak', `Streak updated: ${newStreak} days, action: ${streakAction}`);

                return {
                    success: true,
                    newStreak,
                    streakAction,
                    longestStreak: Math.max(longestStreak, newStreak)
                };
            });

            // ========== POST-TRANSACTION ==========
            if (result.success) {
                // Track analytics
                trackEvent('daily_streak_updated', {
                    new_streak: result.newStreak,
                    action: result.streakAction
                });

                // Show celebration for milestones
                if ([7, 14, 30, 100, 365].includes(result.newStreak)) {
                    trackEvent('streak_milestone_achieved', {
                        streak: result.newStreak,
                        profileId: activeProfile.id
                    });
                }
            }

            return result;

        } catch (error) {
            logger.error('updateDailyStreak', 'Failed to update daily streak', error);
            
            // Queue for offline sync if network issue
            if (error.code === 'unavailable' || error.code === 'failed-precondition') {
                logger.info('updateDailyStreak', 'Network error detected, queuing streak update for offline sync');
                
                await offlineManager.queueForSync('streak_update', {
                    profileId: activeProfile.id,
                    timestamp: Date.now()
                }, 'high'); // High priority
            }
            
            return { success: false, error: error.message };
        } finally {
            // Always reset the flag when done
            updateDailyStreakRef.current = false;
        }
    }, [getDocRef, activeProfile, db]);

    // Offline-aware progress saving function
    const saveProgressOfflineAware = useCallback(async (progressData) => {
        // This function will be used by components to save progress with offline support
        // It will be integrated with the offline context when available
        try {
            const docRef = getDocRef();
            if (!docRef) return;
            await updateDoc(docRef, progressData);
        } catch (error) {
            logger.error('OFFLINE', 'Failed to save progress (offline mode will handle)', error);
            // The offline system will handle this automatically
        }
    }, [getDocRef]);

    // Development mode helper function to clear progress data
    const clearDevelopmentProgress = useCallback(() => {
        if (window.location.hostname === 'localhost') {
            localStorage.removeItem('speak-roots-dev-progress');
            setProgress(null);
            console.log('Development: Progress data cleared');
        }
    }, []);

    // Make clearDevelopmentProgress available in browser console for testing
    useEffect(() => {
        if (window.location.hostname === 'localhost') {
            window.clearDevelopmentProgress = clearDevelopmentProgress;
            console.log('Development: clearDevelopmentProgress function available in console');
        }
    }, [clearDevelopmentProgress]);

    // NEW TRIAL MANAGEMENT FUNCTIONS
    
    /**
     * Check if trial has expired
     */
    const isTrialExpired = useCallback((profile) => {
        if (!profile?.trialEndDate) return false;
        const now = new Date();
        const trialEnd = new Date(profile.trialEndDate);
        return now > trialEnd;
    }, []);

    // updateUserState function removed - now using dynamic state derivation in activeProfile

    /**
     * Check game zone play limits
     */
    const checkGameZonePlayLimit = useCallback((profileId) => {
        const profile = progress?.profiles?.[profileId];
        if (!profile) return { canPlay: false, playsRemaining: 0 };
        
        const today = new Date().toISOString().split('T')[0];
        const lastPlayDate = profile.lastGameZonePlayDate;
        
        // Reset daily plays if it's a new day
        if (lastPlayDate !== today) {
            return { canPlay: true, playsRemaining: 3 };
        }
        
        const playsUsed = profile.gameZonePlaysToday || 0;
        const playsRemaining = Math.max(0, 3 - playsUsed);
        
        return {
            canPlay: playsRemaining > 0,
            playsRemaining,
            lastPlayTimestamp: profile.lastGameZonePlayTimestamp
        };
    }, [progress]);

    /**
     * Record a game zone play
     */
    const recordGameZonePlay = useCallback(async (profileId) => {
        const profile = progress?.profiles?.[profileId];
        if (!profile) return;
        
        const now = new Date();
        const today = now.toISOString().split('T')[0];
        const lastPlayDate = profile.lastGameZonePlayDate;
        
        // Reset daily plays if it's a new day
        if (lastPlayDate !== today) {
            await updateProfileState('gameZonePlaysToday', 1, profileId);
            await updateProfileState('lastGameZonePlayDate', today, profileId);
            await updateProfileState('lastGameZonePlayTimestamp', now.toISOString(), profileId);
        } else {
            const currentPlays = profile.gameZonePlaysToday || 0;
            await updateProfileState('gameZonePlaysToday', currentPlays + 1, profileId);
            await updateProfileState('lastGameZonePlayTimestamp', now.toISOString(), profileId);
        }
    }, [progress, updateProfileState]);

    /**
     * Mark first lesson as completed
     */
    const markFirstLessonCompleted = useCallback(async (profileId) => {
        await updateProfileState('firstLessonCompleted', true, profileId);
        console.log('First lesson completed for profile:', profileId);
    }, [updateProfileState]);

    /**
     * Mark dashboard onboarding as seen
     */
    const markDashboardWelcomeSeen = useCallback(async (profileId) => {
        await updateProfileState('hasSeenDashboardWelcome', true, profileId);
        console.log('Dashboard welcome seen for profile:', profileId);
    }, [updateProfileState]);

    /**
     * Recover progress from activityLog
     * This function restores missing modules from the activityLog array
     * Use this if completedLessons was accidentally cleared but activityLog still has history
     */
    const recoverProgressFromActivityLog = useCallback(async (profileId = null) => {
        const docRef = getDocRef();
        if (!docRef) {
            logger.error('recoverProgressFromActivityLog', 'No document reference available');
            return { success: false, error: 'No doc ref' };
        }

        const targetProfileId = profileId || activeProfile?.id;
        if (!targetProfileId) {
            logger.error('recoverProgressFromActivityLog', 'No profile ID available for recovery');
            return { success: false, error: 'No profile ID' };
        }

        try {
            const result = await runTransaction(db, async (transaction) => {
                const doc = await transaction.get(docRef);
                const data = doc.data();
                
                if (!data) {
                    throw new Error('Progress document not found');
                }

                const profile = data.profiles?.[targetProfileId];
                if (!profile) {
                    throw new Error(`Profile ${targetProfileId} not found`);
                }

                const completedLessons = profile.completedLessons || [];
                const activityLog = profile.activityLog || [];
                
                logger.info('recoverProgressFromActivityLog', `Starting recovery: ${completedLessons.length} completed, ${activityLog.length} in activity log`);
                
                // Extract unique module IDs from activity log
                const activityLogModules = new Set(
                    activityLog.map(a => a.moduleId).filter(Boolean)
                );
                
                // Find missing modules
                const completedSet = new Set(completedLessons);
                const missingModules = Array.from(activityLogModules).filter(id => !completedSet.has(id));
                
                if (missingModules.length === 0) {
                    logger.info('recoverProgressFromActivityLog', 'No recovery needed, progress is consistent');
                    return { 
                        success: true, 
                        recovered: 0, 
                        message: 'No recovery needed',
                        completedLessonsCount: completedLessons.length,
                        activityLogCount: activityLog.length
                    };
                }
                
                logger.info('recoverProgressFromActivityLog', `Found ${missingModules.length} modules to recover`);
                
                // Restore missing modules
                const recoveredCompletedLessons = [...completedLessons, ...missingModules];
                
                const updates = {
                    [`profiles.${targetProfileId}.completedLessons`]: recoveredCompletedLessons,
                    [`profiles.${targetProfileId}.lastActive`]: serverTimestamp(),
                    lastUpdated: serverTimestamp()
                };
                
                transaction.update(docRef, updates);
                
                logger.info('recoverProgressFromActivityLog', `Recovery complete: ${missingModules.length} modules restored, new total: ${recoveredCompletedLessons.length}`);
                
                return { 
                    success: true, 
                    recovered: missingModules.length, 
                    modules: missingModules,
                    completedLessonsCount: recoveredCompletedLessons.length,
                    activityLogCount: activityLog.length
                };
            });
            
            return result;
        } catch (error) {
            logger.error('recoverProgressFromActivityLog', 'Failed to recover progress from activity log', error);
            return { success: false, error: error.message };
        }
    }, [getDocRef, activeProfile, db]);

    // MULTI-LANGUAGE: Calculate total XP and active languages
    const totalXp = useMemo(() => {
        if (!activeProfile) return 0;
        return calculateTotalXp(activeProfile);
    }, [activeProfile]);
    
    const activeLanguages = useMemo(() => {
        if (!activeProfile) return [];
        return getActiveLanguages(activeProfile);
    }, [activeProfile]);

    return useMemo(() => ({
        progress,
        isLoading,
        activeProfile,
        // MULTI-LANGUAGE: Computed values
        totalXp,
        activeLanguages,
        completeOnboarding,
        setCurrentActiveProfile,
        addProfile,
        updateSoundSetting,
        enableAllSounds,
        updateLearningSettings,
        setSubscriptionStatus,
        setSubscriptionOnboardingSeen,
        updateEmailPreferences,
        completeModule,
        updateWeakWords,
        updateDailyStreak,
        // EXPORT NEW FUNCTIONS
        updateProfileState,
        updateInProgressModule,
        clearInProgressModule,
        saveProgressOfflineAware,
        clearDevelopmentProgress,
        // MULTI-LANGUAGE: Language management functions
        switchLanguage,
        addLanguage,
        // NEW TRIAL MANAGEMENT FUNCTIONS
        isTrialExpired,
        checkGameZonePlayLimit,
        recordGameZonePlay,
        markFirstLessonCompleted,
        markDashboardWelcomeSeen,
        // DATA RECOVERY FUNCTION
        recoverProgressFromActivityLog
    }), [progress, isLoading, activeProfile, totalXp, activeLanguages, completeOnboarding, setCurrentActiveProfile, addProfile, updateSoundSetting, enableAllSounds, updateLearningSettings, setSubscriptionStatus, setSubscriptionOnboardingSeen, updateEmailPreferences, completeModule, updateWeakWords, updateDailyStreak, updateProfileState, updateInProgressModule, clearInProgressModule, saveProgressOfflineAware, clearDevelopmentProgress, switchLanguage, addLanguage, isTrialExpired, checkGameZonePlayLimit, recordGameZonePlay, markFirstLessonCompleted, markDashboardWelcomeSeen, recoverProgressFromActivityLog]);
};
